import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Dices, Skull, User, Copy, FileText, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { rollNpc, rollFoe, formatNpcBlock, formatFoeBlockLight, formatFoeBlockFull } from "@/lib/bestiary";

const TABS = ["NPC", "FOE"];

function Row({ k, v }) {
  return (
    <div className="grid grid-cols-[80px_1fr] gap-3 py-1 items-baseline">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">{k}</div>
      <div className="font-mono text-sm text-[color:var(--text-primary)]">{v}</div>
    </div>
  );
}

function StatCell({ k, v }) {
  const sign = v >= 0 ? "+" : "";
  return (
    <div className="border-2 border-[color:var(--border-hard)] p-1 text-center">
      <div className="text-[8px] font-mono uppercase text-[color:var(--text-muted)]">{k}</div>
      <div className="font-display text-lg text-[color:var(--accent-yellow)]">{sign}{v}</div>
    </div>
  );
}

export default function Bestiary({ open, onClose, hasCharacter, onNote }) {
  const [tab, setTab] = useState("NPC");
  const [foeDetail, setFoeDetail] = useState("full"); // "light" | "full"
  const [current, setCurrent] = useState(null);

  const roll = () => {
    const next = tab === "NPC" ? rollNpc() : rollFoe();
    setCurrent(next);
  };

  // Reset current when switching tabs
  const switchTab = (t) => {
    setTab(t);
    setCurrent(null);
  };

  const block = useMemo(() => {
    if (!current) return "";
    if (current.kind === "npc") return formatNpcBlock(current);
    return foeDetail === "light" ? formatFoeBlockLight(current) : formatFoeBlockFull(current);
  }, [current, foeDetail]);

  const copyClipboard = async () => {
    if (!block) return;
    try {
      await navigator.clipboard.writeText(block);
      toast.success("COPIED TO CLIPBOARD");
    } catch {
      toast.error("COPY FAILED", { description: "Browser blocked clipboard access." });
    }
  };

  const copyToNotes = () => {
    if (!block) return;
    if (!hasCharacter) {
      toast.error("NO PUNK LOADED", { description: "Open a character sheet first." });
      return;
    }
    onNote?.(block);
    toast.success(`${current.kind === "npc" ? "NPC" : "FOE"} APPENDED TO NOTES`);
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
            transition={{ type: "spring", damping: 22 }}
            className="relative panel panel-magenta w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
            data-testid="bestiary-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b-2 border-[color:var(--border-hard)] bg-[color:var(--bg-panel)]">
              <div className="flex items-center gap-3">
                <Skull className="text-[color:var(--accent-magenta)]" />
                <div className="font-display text-xl sm:text-2xl font-black uppercase tracking-tighter">
                  BESTIARY//GEN
                </div>
                <span className="tape tape-cyan text-[10px] hidden sm:inline">GM TOOL</span>
              </div>
              <button
                data-testid="bestiary-close"
                onClick={onClose}
                aria-label="Close"
                className="p-2 hover:bg-[color:var(--accent-magenta)] hover:text-[color:var(--bg-void)]"
              >
                <X size={18} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 px-4 sm:px-6 py-3 border-b-2 border-[color:var(--border-hard)]">
              {TABS.map((t) => (
                <button
                  key={t}
                  data-testid={`bestiary-tab-${t.toLowerCase()}`}
                  onClick={() => switchTab(t)}
                  className={`px-3 py-2 border-2 text-xs font-bold uppercase tracking-widest ${
                    tab === t
                      ? "bg-[color:var(--accent-magenta)] text-[color:var(--bg-void)] border-[color:var(--accent-magenta)]"
                      : "border-[color:var(--border-hard)] text-[color:var(--text-secondary)] hover:border-[color:var(--accent-magenta)]"
                  }`}
                >
                  {t === "NPC" ? <User size={12} className="inline mr-1" /> : <Skull size={12} className="inline mr-1" />}
                  {t}
                </button>
              ))}
              {tab === "FOE" && (
                <div className="ml-auto flex items-center gap-1">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)] mr-1">
                    DETAIL
                  </span>
                  {["light", "full"].map((d) => (
                    <button
                      key={d}
                      data-testid={`bestiary-detail-${d}`}
                      onClick={() => setFoeDetail(d)}
                      className={`px-3 py-2 border-2 text-[10px] font-bold uppercase tracking-widest ${
                        foeDetail === d
                          ? "bg-[color:var(--accent-yellow)] text-[color:var(--bg-void)] border-[color:var(--accent-yellow)]"
                          : "border-[color:var(--border-hard)] text-[color:var(--text-secondary)] hover:border-[color:var(--accent-yellow)]"
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              {!current && (
                <div className="text-center py-16">
                  <Dices className="mx-auto text-[color:var(--accent-magenta)] mb-3" size={48} />
                  <div className="font-display uppercase font-black text-2xl">
                    {tab === "NPC" ? "ROLL A STRANGER" : "ROLL A THREAT"}
                  </div>
                  <div className="font-mono text-xs text-[color:var(--text-secondary)] uppercase tracking-widest mt-2">
                    {tab === "NPC"
                      ? "A random civilian, contact, or oddity from the streets."
                      : "A random hostile with full statblock, HP, armour and weapons."}
                  </div>
                  <button
                    data-testid="bestiary-roll-btn"
                    onClick={roll}
                    className="cy-btn cy-btn-solid-yellow mt-6"
                  >
                    <Dices size={16} className="mr-2" /> ROLL {tab}
                  </button>
                </div>
              )}

              {current && current.kind === "npc" && (
                <div className="panel panel-cyan p-4" data-testid="bestiary-npc-card">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--accent-cyan)]">
                    // {current.type}
                  </div>
                  <div className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tighter mb-3">
                    {current.name}
                  </div>
                  <Row k="LOOK" v={current.look} />
                  <Row k="QUIRK" v={current.quirk} />
                  <Row k="MOTIVE" v={current.motive} />
                  <Row k="WANTS" v={current.want} />
                </div>
              )}

              {current && current.kind === "foe" && (
                <div className="panel panel-magenta p-4 space-y-4" data-testid="bestiary-foe-card">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--accent-magenta)]">
                      // {current.type} · MORALE {current.morale}
                    </div>
                    <div className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tighter">
                      {current.name}
                    </div>
                    <div className="font-mono text-xs text-[color:var(--text-secondary)] mt-1">
                      {current.desc}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <div className="border-2 border-[color:var(--accent-magenta)] p-2 text-center">
                      <div className="text-[8px] font-mono uppercase text-[color:var(--text-muted)]">HP</div>
                      <div className="font-display text-2xl text-[color:var(--accent-magenta)]">
                        {current.hp}<span className="text-xs text-[color:var(--text-muted)]">/{current.hp_max}</span>
                      </div>
                      <div className="text-[8px] font-mono text-[color:var(--text-muted)]">{current.hp_die}</div>
                    </div>
                    <div className="border-2 border-[color:var(--accent-cyan)] p-2 text-center">
                      <div className="text-[8px] font-mono uppercase text-[color:var(--text-muted)]">ARMOR</div>
                      <div className="font-display text-2xl text-[color:var(--accent-cyan)]">
                        {current.armor > 0 ? `−d${current.armor}` : "0"}
                      </div>
                      <div className="text-[8px] font-mono text-[color:var(--text-muted)]">{current.armor_die}</div>
                    </div>
                    <div className="border-2 border-[color:var(--accent-yellow)] p-2 text-center col-span-2 sm:col-span-1">
                      <div className="text-[8px] font-mono uppercase text-[color:var(--text-muted)]">MORALE</div>
                      <div className="font-display text-2xl text-[color:var(--accent-yellow)]">{current.morale}</div>
                      <div className="text-[8px] font-mono text-[color:var(--text-muted)]">DR12 or flee</div>
                    </div>
                  </div>

                  {foeDetail === "full" && (
                    <>
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)] mb-1">
                          STATS
                        </div>
                        <div className="grid grid-cols-5 gap-1">
                          <StatCell k="PRS" v={current.stats.presence} />
                          <StatCell k="STR" v={current.stats.strength} />
                          <StatCell k="AGI" v={current.stats.agility} />
                          <StatCell k="TOU" v={current.stats.toughness} />
                          <StatCell k="KNO" v={current.stats.knowledge} />
                        </div>
                      </div>

                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)] mb-1">
                          WEAPONS
                        </div>
                        <ul className="space-y-1" data-testid="bestiary-foe-weapons">
                          {current.weapons.map((w, i) => (
                            <li key={i} className="border-2 border-[color:var(--border-hard)] px-2 py-1 flex items-center justify-between">
                              <span className="font-mono text-sm">{w.name}</span>
                              <span className="tape text-[10px]">{w.damage}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)] mb-1">
                          SPECIALS
                        </div>
                        <ul className="space-y-1 list-disc list-inside font-mono text-xs text-[color:var(--text-primary)]" data-testid="bestiary-foe-specials">
                          {current.specials.map((s, i) => (
                            <li key={i}>{s}</li>
                          ))}
                        </ul>
                      </div>
                    </>
                  )}

                  {foeDetail === "light" && (
                    <>
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)] mb-1">
                          ATTACK
                        </div>
                        <div className="border-2 border-[color:var(--border-hard)] px-2 py-1 flex items-center justify-between">
                          <span className="font-mono text-sm">{current.weapon.name}</span>
                          <span className="tape text-[10px]">{current.weapon.damage}</span>
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)] mb-1">
                          SPECIAL
                        </div>
                        <div className="font-mono text-xs text-[color:var(--text-primary)]">{current.special}</div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Footer actions */}
            {current && (
              <div className="flex flex-wrap items-center gap-2 px-4 sm:px-6 py-3 border-t-2 border-[color:var(--border-hard)] bg-[color:var(--bg-panel)]">
                <button
                  data-testid="bestiary-reroll-btn"
                  onClick={roll}
                  className="cy-btn cy-btn-magenta text-xs"
                >
                  <RefreshCw size={14} className="mr-1" /> RE-ROLL
                </button>
                <button
                  data-testid="bestiary-copy-btn"
                  onClick={copyClipboard}
                  className="cy-btn cy-btn-cyan text-xs"
                >
                  <Copy size={14} className="mr-1" /> COPY BLOCK
                </button>
                <button
                  data-testid="bestiary-notes-btn"
                  onClick={copyToNotes}
                  disabled={!hasCharacter}
                  className="cy-btn cy-btn-solid-yellow text-xs disabled:opacity-50"
                  title={hasCharacter ? "Append to this character's NOTES" : "Open a character to enable"}
                >
                  <FileText size={14} className="mr-1" /> APPEND TO NOTES
                </button>
                <div className="ml-auto text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)]">
                  Ephemeral · closes on modal exit
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
